import React from 'react';
import { ArrowLeft, Shield, Mail, MapPin, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-pink-600 hover:text-pink-700 mb-4 transition-colors duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al inicio
          </Link>
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="h-8 w-8 text-pink-600" />
            <h1 className="text-3xl font-bold text-text">Aviso de Privacidad</h1>
          </div>
          <p className="text-gray-600">Última actualización: 9 de Enero de 2023</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8 prose prose-lg max-w-none">
          <div className="space-y-8">
            
            {/* Información de la empresa */}
            <section className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
              <h2 className="text-xl font-semibold text-blue-800 mb-4">Información del Responsable</h2>
              <div className="space-y-2 text-blue-700">
                <p><strong>Empresa:</strong> SolterosMexico.com (también conocido como "Solteros Mexico")</p>
                <div className="flex items-start space-x-2">
                  <MapPin className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <p><strong>Domicilio:</strong> Calle 1020 La Terraza Cir., Colonia Triana</p>
                    <p>Ciudad Corona, C.P. 98689, California, Estados Unidos de América</p>
                  </div>
                </div>
                <p><strong>Portal:</strong> http://www.SolterosMexico.com</p>
              </div>
            </section>

            {/* Fines de uso */}
            <section>
              <h2 className="text-2xl font-semibold text-text mb-4">¿Para qué fines utilizaremos sus datos personales?</h2>
              <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2">
                <li>Para que usted pueda abrir una cuenta personal en nuestro sitio</li>
                <li>Para que nuestro servidor (computadora) pueda procesar su cuenta y sus mensajes</li>
                <li>Para poder mantener una comunicación con usted, así como también avisarle cuando tenga un nuevo mensaje en su cuenta</li>
                <li>Para ocasionalmente invitarlo a visitar el sitio o avisarle de novedades que puedan ser de su interés</li>
                <li>Para fines de Mercadotecnia y publicidad</li>
              </ul>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
                <p className="text-yellow-800 font-medium">
                  <strong>Importante:</strong> En caso de que no desee que sus datos personales se utilicen para estos fines, no podrá usar el sitio ni el servicio ofrecido, ya que es indispensable para el uso y funcionamiento de su cuenta personal en el sitio web.
                </p>
              </div>
            </section>

            {/* Datos personales */}
            <section>
              <h2 className="text-2xl font-semibold text-text mb-4">¿Qué datos personales utilizaremos para estos fines?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para llevar a cabo las finalidades descritas en el presente aviso de privacidad, utilizaremos los siguientes datos personales:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-2">Datos Personales Básicos</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Estado Civil</li>
                    <li>• Nacionalidad</li>
                    <li>• Correo electrónico</li>
                    <li>• Edad</li>
                    <li>• Fotografía</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-2">Datos de Perfil</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Pasatiempos</li>
                    <li>• Aficiones</li>
                    <li>• Deportes que practica</li>
                    <li>• Juegos de su interés</li>
                    <li>• Descripción de personalidad</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-2">Datos Físicos y Profesionales</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Características físicas</li>
                    <li>• Datos laborales</li>
                    <li>• Datos académicos</li>
                    <li>• Datos migratorios</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-2">Preferencias de Búsqueda</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Buscar pareja en línea</li>
                    <li>• Hombres solteros</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Derechos ARCO */}
            <section>
              <h2 className="text-2xl font-semibold text-text mb-4">¿Cómo puede acceder, rectificar o cancelar sus datos personales, u oponerse a su uso?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Usted tiene derecho a conocer qué datos personales tenemos de usted, para qué los utilizamos y las condiciones del uso que les damos (Acceso). Asimismo, es su derecho solicitar la corrección de su información personal en caso de que esté desactualizada, sea inexacta o incompleta (Rectificación); que la eliminemos de nuestros registros o bases de datos cuando considere que la misma no está siendo utilizada adecuadamente (Cancelación); así como oponerse al uso de sus datos personales para fines específicos (Oposición). Estos derechos se conocen como derechos ARCO.
              </p>
              <div className="bg-green-50 border-l-4 border-green-400 p-4">
                <h3 className="font-semibold text-green-800 mb-2">Para ejercer sus derechos ARCO:</h3>
                <div className="flex items-center space-x-2 text-green-700">
                  <Mail className="h-5 w-5" />
                  <span>Email: ayuda@solterosmexico.com</span>
                </div>
              </div>
            </section>

            {/* Contacto para derechos ARCO */}
            <section>
              <h2 className="text-2xl font-semibold text-text mb-4">Datos de Contacto para Derechos ARCO</h2>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                <div className="space-y-3 text-blue-700">
                  <div>
                    <strong>a) Nombre de la persona responsable:</strong> Marisol Diaz
                  </div>
                  <div className="flex items-start space-x-2">
                    <MapPin className="h-5 w-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div>
                      <strong>b) Domicilio:</strong> Calle 1020 La terraza cir., colonia triana, ciudad corona, municipio o delegación corona, c.p. 98689, en la entidad de California, país Estados Unidos de America
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    <span><strong>c) Correo electrónico:</strong> ayuda@solterosmexico.com</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Revocación de consentimiento */}
            <section>
              <h2 className="text-2xl font-semibold text-text mb-4">Revocación de Consentimiento</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Usted puede revocar el consentimiento que, en su caso, nos haya otorgado para el tratamiento de sus datos personales. Sin embargo, es importante que tenga en cuenta que no en todos los casos podremos atender su solicitud o concluir el uso de forma inmediata, ya que es posible que por alguna obligación legal requiramos seguir tratando sus datos personales.
              </p>
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <p className="text-yellow-800">
                  <strong>Importante:</strong> Para ciertos fines, la revocación de su consentimiento implicará que no le podamos seguir prestando el servicio que nos solicitó, o la conclusión de su relación con nosotros.
                </p>
              </div>
              <div className="bg-green-50 border-l-4 border-green-400 p-4 mt-4">
                <h3 className="font-semibold text-green-800 mb-2">Para revocar su consentimiento:</h3>
                <div className="flex items-center space-x-2 text-green-700">
                  <Mail className="h-5 w-5" />
                  <span>Email: ayuda@solterosmexico.com</span>
                </div>
              </div>
            </section>

            {/* Limitación de uso */}
            <section>
              <h2 className="text-2xl font-semibold text-text mb-4">¿Cómo puede limitar el uso o divulgación de su información personal?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Con objeto de que usted pueda limitar el uso y divulgación de su información personal, le ofrecemos los siguientes medios:
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <div className="flex items-center space-x-2 text-blue-700">
                  <Mail className="h-5 w-5" />
                  <span>Email oficial: ayuda@solterosmexico.com</span>
                </div>
              </div>
            </section>

            {/* Tecnologías de rastreo */}
            <section>
              <h2 className="text-2xl font-semibold text-text mb-4">Uso de Tecnologías de Rastreo</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Le informamos que en nuestra página de internet utilizamos cookies, web beacons u otras tecnologías, a través de las cuales es posible monitorear su comportamiento como usuario de internet, así como brindarle un mejor servicio y experiencia al navegar en nuestra página.
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h3 className="font-semibold text-gray-800 mb-2">Fines de las tecnologías de rastreo:</h3>
                <p className="text-gray-600">Los datos serán usados para administrar su sesión en el sitio, para avisarle de nuevos mensajes, para poder proporcionarle una cuenta personal y para ocasionalmente mandarle avisos directamente a su correo electrónico.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">Datos obtenidos de estas tecnologías:</h3>
                <ul className="list-disc list-inside text-gray-600 space-y-1">
                  <li>Identificadores, dirección IP, nombre de usuario y contraseñas de una sesión</li>
                  <li>Región en la que se encuentra el usuario</li>
                  <li>Fecha y hora del inicio y final de una sesión de un usuario</li>
                  <li>Publicidad revisada por un usuario</li>
                </ul>
              </div>
            </section>

            {/* Cambios en el aviso */}
            <section>
              <h2 className="text-2xl font-semibold text-text mb-4">¿Cómo puede conocer los cambios en este aviso de privacidad?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                El presente aviso de privacidad puede sufrir modificaciones, cambios o actualizaciones derivadas de nuevos requerimientos legales; de nuestras propias necesidades por los productos o servicios que ofrecemos; de nuestras prácticas de privacidad; de cambios en nuestro modelo de negocio, o por otras causas.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                <p className="text-blue-700">
                  <strong>Nos comprometemos a mantenerlo informado</strong> sobre los cambios que pueda sufrir el presente aviso de privacidad, a través de la publicación de los cambios o actualizaciones directamente en el sitio web.
                </p>
              </div>
            </section>

            {/* Información de contacto final */}
            <div className="bg-pink-50 border-l-4 border-pink-400 p-6 rounded-r-lg mt-8">
              <h3 className="text-lg font-semibold text-pink-800 mb-4">Contacto</h3>
              <div className="space-y-2 text-pink-700">
                <div className="flex items-center space-x-2">
                  <Mail className="h-5 w-5" />
                  <span>Email: ayuda@solterosmexico.com</span>
                </div>
                <div className="flex items-start space-x-2">
                  <MapPin className="h-5 w-5 mt-1 flex-shrink-0" />
                  <div>
                    <p>Domicilio: Calle 1020 La Terraza Cir., Colonia Triana</p>
                    <p>Ciudad Corona, C.P. 98689, California, Estados Unidos</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <span>Última actualización: 9 de Enero de 2023</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
